import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import BottomTabBar from './BottomTabBar';
import TurboModal from '../analysis/TurboModal';
import { useAuth } from '../../store/AuthContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import axios from 'axios';
import { getApiUrl } from '../../services/api';

const DashboardLayout = ({ children }) => {
  const { user, token, setUser } = useAuth();
  const isMobile = useIsMobile();
  const [showTurbo, setShowTurbo] = useState(null); // { type, collection } or null
  const [activeFilter, setActiveFilter] = useState(null);
  const [scoutActive, setScoutActive] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hiddenBrands, setHiddenBrands] = useState(new Set()); // Set of "brand|type"
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Close the mobile drawer whenever we switch to desktop.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // Load hidden brands from user profile when component mounts or user changes
  useEffect(() => {
    if (user?.missionProfile?.hiddenBrands) {
      // Normalize existing hidden brands to ensure cross-consistency
      const normalized = user.missionProfile.hiddenBrands.map(key => {
        const [brand, type] = key.split('|');
        if (!brand) return key;
        const normalizedBrand = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
        return `${normalizedBrand}|${type || 'base'}`;
      });
      setHiddenBrands(new Set(normalized));
    }
  }, [user?.id]);

  const handleBrandSelect = (brand, type) => {
    setActiveFilter({ brand, type });
  };

  const toggleBrandVisibility = async (brand, type) => {
    const key = `${brand}|${type}`;
    let nextSet;
    
    setHiddenBrands(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      nextSet = next;
      return next;
    });

    // Sync with backend
    try {
      const resp = await axios.put(`${getApiUrl()}/auth/profile`, {
        missionProfile: {
          ...user.missionProfile,
          hiddenBrands: Array.from(nextSet)
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(resp.data.data.user);
    } catch (err) {
      console.error('Failed to sync brand visibility:', err);
    }
  };

  const toggleCategoryVisibility = async (type, categoryBrands) => {
    let nextSet;
    const anyVisible = categoryBrands.some(b => !hiddenBrands.has(`${b}|${type}`));
    
    setHiddenBrands(prev => {
      const next = new Set(prev);
      categoryBrands.forEach(brand => {
        const key = `${brand}|${type}`;
        if (anyVisible) {
          next.add(key); // Hide all if any were visible
        } else {
          next.delete(key); // Show all if none were visible
        }
      });
      nextSet = next;
      return next;
    });

    // Sync with backend
    try {
      const resp = await axios.put(`${getApiUrl()}/auth/profile`, {
        missionProfile: {
          ...user.missionProfile,
          hiddenBrands: Array.from(nextSet)
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(resp.data.data.user);
    } catch (err) {
      console.error('Failed to sync category visibility:', err);
    }
  };

  const triggerRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="grid-bg" style={{ minHeight: '100vh', backgroundColor: '#05070A', position: 'relative', overflow: 'hidden' }}>
      <div className="scanline" />
      <Navbar
        onRefreshNeeded={triggerRefresh}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearchTriggered={() => setSearchTrigger(prev => prev + 1)}
        isMobile={isMobile}
        onHamburger={() => setDrawerOpen(true)}
      />
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <Sidebar
          onTurboClick={(config) => setShowTurbo(config || { type: 'base', collection: 'base' })}
          onBrandSelect={handleBrandSelect}
          scoutActive={scoutActive}
          onScoutToggle={() => setScoutActive(!scoutActive)}
          onRefreshNeeded={triggerRefresh}
          hiddenBrands={hiddenBrands}
          onToggleVisibility={toggleBrandVisibility}
          onToggleCategoryVisibility={toggleCategoryVisibility}
          collapsed={!isMobile && sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          variant={isMobile ? 'drawer' : 'fixed'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
        <main style={{
          marginTop: '60px',
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? '60px' : '260px'),
          transition: 'margin-left 0.3s ease',
          height: 'calc(100vh - 60px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: isMobile ? '64px' : 0,
          backgroundColor: 'transparent'
        }}>
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                activeFilter,
                scoutActive,
                refreshKey,
                hiddenBrands,
                onToggleBrandVisibility: toggleBrandVisibility,
                searchQuery,
                searchTrigger
              });
            }
            return child;
          })}
        </main>
      </div>

      {isMobile && <BottomTabBar onMore={() => setDrawerOpen(true)} />}

      <TurboModal 
        isOpen={!!showTurbo} 
        onClose={() => setShowTurbo(null)} 
        initialConfig={showTurbo}
        onImportComplete={() => {
          triggerRefresh();
        }} 
      />

      <style>{`
        :root {
          --sidebar-width: 260px;
        }
        /* Handle dynamic margin if we wanted to pass state up, 
           but CSS variables + inline style in Sidebar is better for simple apps */
      `}</style>
    </div>
  );
};

export default DashboardLayout;
